import { createContext, useContext, useEffect, useRef } from 'react';

import { createStore } from 'zustand';
import { immer } from 'zustand/middleware/immer';
import { useStoreWithEqualityFn } from 'zustand/traditional';

import { ConditionBuilderError, Controls } from './types';
import { Field, OptionList, RuleGroupType } from './types';
import { createDefaultCondition, createFieldMap } from './utils';

interface ConditionBuilderStoreProps {
  fields: OptionList<Field>;
  condition: RuleGroupType;
  handleConditionChange: (condition: RuleGroupType) => void;
  errors?: ConditionBuilderError;
  compact: boolean;
  controlElements?: Partial<Controls>;
  hideConditionButtons: boolean;
  disabled: boolean;
}

interface ConditionBuilderStoreState extends ConditionBuilderStoreProps {
  fieldMap: Map<string, Field>;
}

type ConditionBuilderStore = ReturnType<typeof createConditionBuilderStore>;

const createConditionBuilderStore = ({
  fields,
  condition,
  handleConditionChange,
  compact,
  hideConditionButtons,
  disabled,
}: ConditionBuilderStoreProps) => {
  return createStore<ConditionBuilderStoreState>()(
    immer(() => ({
      hideConditionButtons,
      fields,
      fieldMap: createFieldMap(fields),
      condition,
      handleConditionChange,
      compact,
      disabled,
    }))
  );
};

const ConditionBuilderContext = createContext<ConditionBuilderStore | null>(null);

export const ConditionBuilderUpdater = ({
  errors,
  controlElements,
}: {
  errors?: ConditionBuilderError;
  controlElements?: Partial<Controls>;
}) => {
  const store = useConditionBuilderStoreApi();

  useEffect(() => {
    store.setState({ errors });
  }, [errors, store]);

  useEffect(() => {
    store.setState({ controlElements });
  }, [controlElements, store]);

  // eslint-disable-next-line react/jsx-no-useless-fragment
  return <></>;
};

export function ConditionBuilderContextProvider({
  children,
  fields = [],
  condition = createDefaultCondition(),
  handleConditionChange = () => {
    console.error('handleConditionChange is not defined');
  },
  errors,
  compact = false,
  hideConditionButtons = false,
  disabled = false,
}: React.PropsWithChildren<Partial<ConditionBuilderStoreProps>>) {
  const storeRef = useRef<ConditionBuilderStore>(null);

  if (!storeRef.current) {
    storeRef.current = createConditionBuilderStore({
      fields,
      condition,
      handleConditionChange,
      errors,
      compact,
      hideConditionButtons,
      disabled,
    });
  }

  return (
    <ConditionBuilderContext.Provider value={storeRef.current}>
      {children}
    </ConditionBuilderContext.Provider>
  );
}

export function useConditionBuilderStoreApi() {
  const store = useContext(ConditionBuilderContext);

  if (!store) throw new Error('Missing ConditionBuilderContext.Provider in the tree');

  return store;
}

export function useConditionBuilderStore<T>(
  selector: (state: ConditionBuilderStoreState) => T,
  equalityFn?: (left: T, right: T) => boolean
): T {
  const store = useConditionBuilderStoreApi();

  return useStoreWithEqualityFn(store, selector, equalityFn);
}
